import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { setAppLoading } from "actions/appStateActions";
import IamApi from "api/IamApi";
import { RecentPaymentsContent } from "components/ezpay/secure/common/recent-payments-shared/RecentPaymentsContent";
import { reloadUpcomingPaymentWithState } from "components/ezpay/secure/common/recent-payments-shared/reloadUpcomingPaymentWithState";
import {
  BANK_ACCCOUNT_MODAL,
  PAYMENT_METHOD_MODAL_NAMESPACE,
  RECENT_PAYMENTS_NAMESPACE,
} from "constants/i18n-namespaces";
import { DONE, EMPTY, ERROR, IN_PROGRESS } from "constants/Status";
import { useCurrencyFormatter, usePaymentHistory } from "hooks";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useOutletContext } from "react-router-dom";
import routing from "routing";
import { getBiller } from "selectors";
import { getIsMobile } from "selectors/appState";
import { IPaymentRecord } from "types/Api/Payment/paymentRecord";
import { IContactInfoProperties } from "types/biller";
import { ISession } from "types/session";
import { mappingDonataionResponseToCharityState } from "utilities/helper";
import { PaymentContent } from "../recent-payments-shared/PaymentContent";

export const RecentPayments = () => {
  const [confirmCancelModalOpen, setConfirmCancelModalOpen] =
    useState<boolean>(false);
  const { account } = useOutletContext<ISession>();
  const [cancelState, setCancelState] = useState(EMPTY);
  const [cancelErrorMessage, setCancelErrorMessage] = useState<string>();
  const [loadingStatus, setLoadingStatus] = useState(false);

  const { t } = useTranslation([
    RECENT_PAYMENTS_NAMESPACE,
    PAYMENT_METHOD_MODAL_NAMESPACE,
    BANK_ACCCOUNT_MODAL,
  ]);
  const { loadUpcomingPayment, getUpcomingPayment, getRecentPayments } =
    usePaymentHistory();

  const dispatch = useDispatch();
  const isMobile = useSelector(getIsMobile);
  const biller = useSelector(getBiller);
  const navigate = useNavigate();
  const currencyFormatter = useCurrencyFormatter();

  const upcomingPayment = getUpcomingPayment();
  const recentPayments = getRecentPayments();

  const contactUs = biller?.properties?.find((element) => {
    return element.name === "InstanceConfiguration";
  })?.properties as IContactInfoProperties;

  const cancelPaymentIntent = async (
    paymentId: string,
    paymentIntentId: string,
  ) => {
    dispatch(setAppLoading(true));
    try {
      setCancelState(IN_PROGRESS);
      await IamApi.ezpay.cancelUpcomingPayment(
        paymentId,
        paymentIntentId,
        biller.clientId,
        account.realm,
      );
      setCancelState(DONE);
    } catch (error: any) {
      setCancelState(ERROR);
      setCancelErrorMessage(error.message);
    }
    dispatch(setAppLoading(false));
  };

  const reloadUpcomingPayment = () =>
    reloadUpcomingPaymentWithState({
      clientId: biller.clientId,
      realm: account.realm,
      loadUpcomingPayment,
      setLoadingStatus,
      setConfirmCancelModalOpen,
      setCancelState,
      setCancelErrorMessage,
    });

  const createPaymentIntentContent = (
    payment: IPaymentRecord,
    isExpanded: boolean,
  ) => {
    const charities = mappingDonataionResponseToCharityState(
      payment.donations,
      account.charities,
    );
    const selectedCharities = charities?.filter((char) => char.selected);

    return (
      <PaymentContent
        payment={payment}
        upcomingPayment={upcomingPayment}
        isMobile={isMobile}
        selectedCharities={selectedCharities}
        isUpcomingCancellable={!!upcomingPayment?.isCancellable}
        contactUs={contactUs}
        currencyFormatter={currencyFormatter}
        onCancelClick={() => setConfirmCancelModalOpen(true)}
        isExpanded={isExpanded}
      />
    );
  };

  return (
    <div className="payment-receipt-frame" style={{ gap: 0 }}>
      <div
        className="common-header payment-receipt-frame-header"
        style={{ width: "100%" }}
      >
        <div className="back-to-payment-dashboard">
          <button
            className="ez-pay-button-link"
            onClick={() => navigate(routing.balance.index())}
            data-cy="back-to-payments"
          >
            <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
            <span> {t("back-to-payment-dashboard")}</span>
          </button>
        </div>
      </div>
      <RecentPaymentsContent
        loadingStatus={loadingStatus}
        upcomingPayment={upcomingPayment}
        recentPayments={recentPayments}
        createPaymentIntentContent={createPaymentIntentContent}
        biller={biller}
        confirmCancelModalOpen={confirmCancelModalOpen}
        isMobile={isMobile}
        cancelState={cancelState}
        cancelErrorMessage={cancelErrorMessage}
        onCancelModalClose={() => setConfirmCancelModalOpen(false)}
        onConfirmCancel={() =>
          cancelPaymentIntent(
            upcomingPayment.id,
            upcomingPayment.paymentIntentId,
          )
        }
        onReload={reloadUpcomingPayment}
      />
    </div>
  );
};

export default RecentPayments;
